import { createXXHash64 } from 'hash-wasm';
import { isValidEmail } from '../../shared/emailValidation';

const DEFAULT_DELIMITER = ',';
const MAX_INVALID_ROWS = 5;
export const MAX_PREVIEW_ERRORS_PER_CATEGORY = 5;
const MAX_SLICE_MS = 350;
const CHECK_EVERY = 50;

const nextFrame = () => new Promise((resolve) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => resolve());
  } else {
    setTimeout(resolve, 0);
  }
});

const normalizeHeader = (value) => value.replace(/^\uFEFF/, '').trim();

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) {
    return null;
  }
  const normalized = options
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return normalized.length > 0 ? normalized : null;
};

const normalizeHeaderDefinition = (header) => {
  if (typeof header === 'string') {
    return { name: header, allowEmpty: false, options: null };
  }
  if (header && typeof header === 'object') {
    const name = header.name || header.field || '';
    if (!name) {
      return null;
    }
    return {
      name,
      allowEmpty: Boolean(header.allowEmpty),
      options: normalizeOptions(header.options || header.allowedValues),
    };
  }
  return null;
};

export const escapeCsvValue = (value) => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const normalizeHeaderDefinitions = (headers = []) => {
  if (!Array.isArray(headers)) {
    return [];
  }
  return headers
    .map(normalizeHeaderDefinition)
    .filter(Boolean);
};

export const parseCsvLine = (line, delimiter = DEFAULT_DELIMITER) => {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      const nextChar = line[i + 1];
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      fields.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  if (inQuotes) {
    return { error: 'UNBALANCED_QUOTES' };
  }

  fields.push(current);
  return { fields };
};

const countRecordLines = (record) => {
  if (!record) return 1;
  let count = 1;
  for (let i = 0; i < record.length; i += 1) {
    const char = record[i];
    if (char === '\n') {
      count += 1;
      continue;
    }
    if (char === '\r') {
      if (record[i + 1] === '\n') {
        i += 1;
      }
      count += 1;
    }
  }
  return count;
};

const extractCsvRecords = (text) => {
  const records = [];
  let recordStart = 0;
  let inQuotes = false;
  let lineCount = 1;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '"') {
      const nextChar = text[i + 1];
      if (inQuotes && nextChar === '"') {
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === '\n' || char === '\r') {
      if (inQuotes) {
        if (char === '\r' && text[i + 1] === '\n') {
          i += 1;
        }
        lineCount += 1;
        continue;
      }

      const record = text.slice(recordStart, i);
      records.push({ record, lineCount });
      if (char === '\r' && text[i + 1] === '\n') {
        i += 1;
      }
      recordStart = i + 1;
      lineCount = 1;
    }
  }

  return {
    records,
    remainder: text.slice(recordStart),
    remainderLineCount: lineCount,
  };
};

const createCsvHasher = async () => {
  const hasher = await createXXHash64();
  hasher.init();
  return hasher;
};

const createValidator = ({ headers, onProgress, maxInvalidRows = MAX_INVALID_ROWS }) => {
  const headerDefinitions = normalizeHeaderDefinitions(headers);
  const normalizedHeaders = headerDefinitions.map((header) => normalizeHeader(header.name));
  const allowEmptyHeaders = new Set(
    headerDefinitions
      .filter((header) => header.allowEmpty)
      .map((header) => normalizeHeader(header.name)),
  );
  const optionLookup = new Map();
  const optionLists = new Map();
  headerDefinitions.forEach((header) => {
    if (Array.isArray(header.options) && header.options.length > 0) {
      const cleanedOptions = header.options
        .map((option) => String(option).trim())
        .filter(Boolean);
      if (cleanedOptions.length > 0) {
        const normalizedName = normalizeHeader(header.name);
        optionLookup.set(normalizedName, new Set(cleanedOptions));
        optionLists.set(normalizedName, cleanedOptions);
      }
    }
  });
  console.info('[bulk-upload] CSV required headers:', normalizedHeaders);
  const state = {
    headerValidated: false,
    rowCount: 0,
    invalidRowCount: 0,
    invalidRows: [],
    fatalError: null,
    lineNumber: 0,
    errorCount: 0,
    errorCategories: new Map(),
  };
  let headerIndexMap = new Map();
  let headerFieldCount = normalizedHeaders.length;

  const serializeErrorCategories = () => (
    Array.from(state.errorCategories.values()).map((category) => ({
      key: category.key,
      kind: category.kind,
      field: category.field,
      count: category.count,
      preview: [...category.preview],
    }))
  );

  const getSnapshot = () => ({
    rowCount: state.rowCount,
    invalidRowCount: state.invalidRowCount,
    invalidRows: [...state.invalidRows],
    fatalError: state.fatalError,
    lineNumber: state.lineNumber,
    errorCount: state.errorCount,
    errorCategories: serializeErrorCategories(),
  });

  const emitProgress = () => {
    if (onProgress) {
      onProgress(getSnapshot());
    }
  };

  const setFatalError = (error) => {
    state.fatalError = error;
    emitProgress();
  };

  const recordInvalidRow = (
    lineNumber,
    {
      blankFields = [],
      invalidOptions = [],
      invalidFormats = [],
    },
  ) => {
    state.invalidRowCount += 1;
    if (state.invalidRows.length < maxInvalidRows) {
      state.invalidRows.push({
        lineNumber,
        blankFields,
        invalidOptions,
        invalidFormats,
      });
    }
    emitProgress();
  };

  const recordCategoryError = ({
    categoryKey,
    kind,
    field,
    lineNumber,
    value,
    message,
  }) => {
    let category = state.errorCategories.get(categoryKey);
    if (!category) {
      category = {
        key: categoryKey,
        kind,
        field: field || null,
        count: 0,
        preview: [],
      };
      state.errorCategories.set(categoryKey, category);
    }
    category.count += 1;
    state.errorCount += 1;
    if (category.preview.length < MAX_PREVIEW_ERRORS_PER_CATEGORY) {
      category.preview.push({
        lineNumber,
        field: field || null,
        value: value || '',
        message,
      });
    }
  };

  const handleLine = (rawLine, lineCount = 1) => {
    if (state.fatalError) return;
    const lineStart = state.lineNumber + 1;
    state.lineNumber += lineCount;

    const line = rawLine.replace(/\r$/, '');
    if (!state.headerValidated) {
      if (line.trim() === '') {
        return;
      }
      const parsed = parseCsvLine(line);
      if (parsed.error) {
        setFatalError({
          type: parsed.error,
          message: 'CSV header row is not readable.',
          lineNumber: lineStart,
        });
        return;
      }
      const headerFields = parsed.fields.map((field, index) => (
        index === 0 ? normalizeHeader(field) : field.trim()
      ));
      console.info('[bulk-upload] CSV file headers:', headerFields);
      const normalizedHeaderFields = headerFields.map((field) => normalizeHeader(field));
      const headerSet = new Set(normalizedHeaderFields);
      const matches = normalizedHeaderFields.length === normalizedHeaders.length
        && normalizedHeaders.every((field) => headerSet.has(field));
      if (!matches) {
        setFatalError({
          type: 'HEADER_MISMATCH',
          message: 'CSV headers do not match the required format.',
          lineNumber: lineStart,
        });
        return;
      }
      headerIndexMap = new Map(
        normalizedHeaderFields.map((field, index) => [field, index]),
      );
      headerFieldCount = normalizedHeaderFields.length;
      state.headerValidated = true;
      emitProgress();
      return;
    }

    if (line.trim() === '') {
      return;
    }

    const parsed = parseCsvLine(line);
    if (parsed.error) {
      setFatalError({
        type: parsed.error,
        message: 'CSV row has unbalanced quotes.',
        lineNumber: lineStart,
      });
      return;
    }

    let fields = parsed.fields;
    if (fields.length < headerFieldCount) {
      fields = [...fields];
      while (fields.length < headerFieldCount) {
        fields.push('');
      }
    } else if (fields.length > headerFieldCount) {
      const extraFields = fields.slice(headerFieldCount);
      const hasExtraValues = extraFields.some((value) => value.trim() !== '');
      if (hasExtraValues) {
        setFatalError({
          type: 'COLUMN_MISMATCH',
          message: 'CSV row has the wrong number of columns.',
          lineNumber: lineStart,
        });
        return;
      }
      fields = fields.slice(0, headerFieldCount);
    }

    state.rowCount += 1;

    const trimmedFields = fields.map((value) => value.trim());
    const orderedFields = normalizedHeaders.map(
      (name) => trimmedFields[headerIndexMap.get(name)] || '',
    );
    const fieldValues = normalizedHeaders.reduce((acc, name, index) => {
      acc[name] = orderedFields[index] || '';
      return acc;
    }, {});
    const hasIndividualName = !!fieldValues.individualName;
    const hasBusinessName = !!fieldValues.businessName;
    const blankFields = normalizedHeaders.filter((name, index) => {
      const value = orderedFields[index];
      if (value) return false;
      if (allowEmptyHeaders.has(name)) return false;
      if (name === 'referenceId') return false;
      if ((name === 'individualName' || name === 'businessName') && (hasIndividualName || hasBusinessName)) {
        return false;
      }
      return true;
    });
    const invalidOptions = [];
    normalizedHeaders.forEach((name, index) => {
      const allowedOptions = optionLookup.get(name);
      if (!allowedOptions) return;
      const value = orderedFields[index];
      if (!value) return;
      if (!allowedOptions.has(value)) {
        invalidOptions.push({
          field: name,
          value,
          options: optionLists.get(name) || [],
        });
      }
    });
    const invalidFormats = [];
    const individualEmail = fieldValues.individualEmail;
    if (individualEmail && !isValidEmail(individualEmail)) {
      invalidFormats.push({
        field: 'individualEmail',
        value: individualEmail,
        message: 'invalid email format',
      });
    }

    if (blankFields.length > 0 || invalidOptions.length > 0 || invalidFormats.length > 0) {
      blankFields.forEach((field) => {
        recordCategoryError({
          categoryKey: 'requiredFieldsMissing',
          kind: 'missing',
          field,
          lineNumber: lineStart,
          value: field,
          message: 'is required',
        });
      });
      invalidOptions.forEach((invalid) => {
        recordCategoryError({
          categoryKey: `invalid:${invalid.field}`,
          kind: 'invalid_option',
          field: invalid.field,
          lineNumber: lineStart,
          value: invalid.value,
          message: 'not in allowed values',
        });
      });
      invalidFormats.forEach((invalid) => {
        recordCategoryError({
          categoryKey: `invalid:${invalid.field}`,
          kind: 'invalid_format',
          field: invalid.field,
          lineNumber: lineStart,
          value: invalid.value,
          message: invalid.message,
        });
      });
      recordInvalidRow(lineStart, { blankFields, invalidOptions, invalidFormats });
      return;
    }

    emitProgress();
  };

  const finalize = () => {
    if (!state.fatalError && state.rowCount === 0) {
      if (state.headerValidated) {
        setFatalError({
          type: 'NO_DATA_ROWS',
          message: 'CSV must contain at least one data row.',
          lineNumber: state.lineNumber || 1,
        });
      } else {
        setFatalError({
          type: 'EMPTY_FILE',
          message: 'CSV file is empty.',
          lineNumber: state.lineNumber || 1,
        });
      }
    }
    return getSnapshot();
  };

  return { handleLine, finalize, getSnapshot, getState: () => state };
};

export const validateCsvText = async ({ text, headers, onProgress, maxInvalidRows }) => {
  const validator = createValidator({ headers, onProgress, maxInvalidRows });
  const hasher = await createCsvHasher();
  if (text) {
    hasher.update(text);
  }
  const { records, remainder, remainderLineCount } = extractCsvRecords(text);
  for (const { record, lineCount } of records) {
    validator.handleLine(record, lineCount);
    if (validator.getState().fatalError) {
      break;
    }
  }
  if (!validator.getState().fatalError && remainder.length > 0) {
    validator.handleLine(remainder, remainderLineCount || countRecordLines(remainder));
  }
  const result = validator.finalize();
  return {
    ...result,
    fileHash: result.fatalError ? null : hasher.digest(),
  };
};

export const validateCsvFile = async ({
  file,
  headers,
  onProgress,
  signal,
  maxInvalidRows,
}) => {
  const validator = createValidator({ headers, onProgress, maxInvalidRows });
  const hasher = await createCsvHasher();
  let buffer = '';
  let lastYieldTime = typeof performance !== 'undefined' ? performance.now() : 0;
  let activeReader = null;
  let linesSinceCheck = 0;

  const stream = file.stream();
  const textStream = typeof TextDecoderStream === 'function'
    ? stream.pipeThrough(new TextDecoderStream())
    : stream;

  const shouldYield = () => {
    if (typeof performance === 'undefined') return false;
    return performance.now() - lastYieldTime > MAX_SLICE_MS;
  };

  const maybeYield = async () => {
    if (shouldYield()) {
      lastYieldTime = typeof performance !== 'undefined' ? performance.now() : 0;
      await nextFrame();
    }
  };

  const abortIfNeeded = () => {
    if (signal?.aborted) {
      const abortError = new DOMException('Aborted', 'AbortError');
      throw abortError;
    }
  };

  // Best-effort cleanup for stream readers to avoid leaks/locks after errors.
  const cleanupStream = async () => {
    try {
      if (activeReader?.cancel) {
        // Release the reader lock when possible so the stream can be reused.
        await activeReader.cancel();
        if (typeof activeReader.releaseLock === 'function') {
          activeReader.releaseLock();
        }
        return;
      }
      if (typeof textStream?.cancel === 'function') {
        // Fallback for async-iterable text streams.
        await textStream.cancel();
      }
    } catch (cancelError) {
      console.info('[bulk-upload] Failed to cancel CSV stream:', cancelError);
    }
  };

  const handleChunkText = async (chunkText) => {
    if (chunkText) {
      hasher.update(chunkText);
    }
    buffer += chunkText;
    const { records, remainder } = extractCsvRecords(buffer);
    buffer = remainder;

    for (const { record, lineCount } of records) {
      abortIfNeeded();
      validator.handleLine(record, lineCount);
      if (validator.getState().fatalError) {
        return false;
      }
      linesSinceCheck += 1;
      if (linesSinceCheck >= CHECK_EVERY) {
        linesSinceCheck = 0;
        const inputPending = typeof navigator !== 'undefined'
          && navigator.scheduling?.isInputPending?.();
        if (inputPending || shouldYield()) {
          lastYieldTime = typeof performance !== 'undefined' ? performance.now() : 0;
          await nextFrame();
        }
      }
    }
    await maybeYield();
    return true;
  };

  try {
    if (textStream === stream) {
      const reader = stream.getReader();
      activeReader = reader;
      const decoder = new TextDecoder('utf-8');

      while (true) {
        abortIfNeeded();
        const { value, done } = await reader.read();
        if (done) break;
        const chunkText = decoder.decode(value, { stream: true });
        const shouldContinue = await handleChunkText(chunkText);
        if (!shouldContinue) {
          try {
            await reader.cancel();
          } finally {
            const result = validator.finalize();
            return {
              ...result,
              fileHash: null,
            };
          }
        }
      }
    } else {
      for await (const chunk of textStream) {
        abortIfNeeded();
        const shouldContinue = await handleChunkText(chunk);
        if (!shouldContinue) {
          try {
            await textStream.cancel();
          } finally {
            const result = validator.finalize();
            return {
              ...result,
              fileHash: null,
            };
          }
        }
      }
    }

    if (buffer.length > 0) {
      validator.handleLine(buffer, countRecordLines(buffer));
    }

    const result = validator.finalize();
    return {
      ...result,
      fileHash: result.fatalError ? null : hasher.digest(),
    };
  } catch (error) {
    await cleanupStream();
    if (error?.name === 'AbortError') {
      return {
        ...validator.getSnapshot(),
        canceled: true,
        fileHash: null,
      };
    }
    throw error;
  }
};
